import React, { useRef, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Box, Text, Paper, Skeleton, Center, Stack } from "@mantine/core";
import useColors from "../../../theme/useColors";
import { gray } from "../../../constants/color-constants";

const DEFAULT_CHART_WIDTH = 600;
const DEFAULT_CHART_HEIGHT = 400;

const toNumericDimension = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return fallback;
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Custom plugin to style legend items with rounded corners
const legendSquarePlugin: Plugin<any> = {
  id: "legendSquarePlugin",
  beforeDraw: (chart) => {
    const legend = chart.legend;
    if (legend && legend.legendItems) {
      legend.legendItems.forEach((item) => {
        if (item && typeof item.fillStyle === "string") {
          const datasetIndex = item.datasetIndex;
          if (datasetIndex !== undefined && chart.data.datasets[datasetIndex]) {
            chart.data.datasets[datasetIndex].backgroundColor = item.fillStyle;
          }
        }
      });
    }
  },
};

const piePercentagePlugin: Plugin<'pie'> = {
  id: 'piePercentagePlugin',
  afterDatasetsDraw: (chart) => {
    const { ctx, data } = chart;
    const dataset = data.datasets[0];
    const total = (dataset.data as number[]).reduce((sum, val) => sum + val, 0);
    
    if (total === 0) return;
    
    chart.getDatasetMeta(0).data.forEach((element: any, index) => {
      const value = dataset.data[index] as number;
      const percentage = ((value / total) * 100);
      
      if (percentage < 5) return;
      
      const { startAngle, endAngle, innerRadius, outerRadius, x: centerX, y: centerY } = element;
      
      const middleAngle = (startAngle + endAngle) / 2;
      
      // Calculate the position at 70% of the radius from center
      const labelRadius = innerRadius + (outerRadius - innerRadius) * 0.7;
      const labelX = centerX + Math.cos(middleAngle) * labelRadius;
      const labelY = centerY + Math.sin(middleAngle) * labelRadius;
      
      // Calculate font size based on percentage (responsive sizing)
      let fontSize;
      if (percentage >= 20) {
        fontSize = 14;
      } else if (percentage >= 15) {
        fontSize = 12;
      } else if (percentage >= 10) {
        fontSize = 10;
      } else {
        fontSize = 9;
      }
      
      ctx.save();
      ctx.font = ` ${fontSize}px Roboto, sans-serif`;
      ctx.fillStyle = '#262626'; // Black color for percentage text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const text = `${percentage.toFixed(1)}%`;
      
      ctx.fillText(text, labelX, labelY);
      
      ctx.restore();
    });
  },
};

export type ChartType = "bar" | "horizontalBar" | "line" | "pie";

const axisTitleFont = {
  family: "Roboto",
  size: 14,
  weight: "500" as const,
  lineHeight: "24px",
};

const buildAxisTitle = (title?: string | null) => ({
  display: Boolean(title),
  text: title || "",
  font: axisTitleFont,
  padding: {
    top: 0,
    bottom: 0,
  },
});

export interface ChartProps {
  type: ChartType;
  title: string;
  data: ChartData<any>;
  options?: ChartOptions<any>;
  height?: number;
  width?: any;
  xAxisTitle?: string | null;
  yAxisTitle?: string | null;
  loading?: boolean;
  noData?: boolean;
  noDataMessage?: string;
}

const DataChart: React.FC<ChartProps> = ({
  type,
  title,
  data,
  options,
  height = 400,
  width = 600,
  xAxisTitle,
  yAxisTitle,
  loading = false,
  noData = false,
  noDataMessage = "No data available",
}) => {
  const chartRef = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: toNumericDimension(width, DEFAULT_CHART_WIDTH),
    height: toNumericDimension(height, DEFAULT_CHART_HEIGHT),
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const themeColors = useColors();

  const colors = {
    allProvider: themeColors.gray[5],
    ajayHuang: themeColors.success[5],
    queryAgreed: themeColors.success[5],
    queryDisagreed: themeColors.warning[4],
  };

  // Helper function to format numbers
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Responsive size calculation
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;

      const measuredWidth = containerRef.current.offsetWidth;
      const measuredHeight = containerRef.current.offsetHeight;

      if (measuredWidth > 0 && measuredHeight > 0) {
        setContainerSize({
          width: measuredWidth,
          height: measuredHeight,
        });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [type]);

  const numericWidth = toNumericDimension(
    typeof width === "number" ? width : containerSize.width,
    DEFAULT_CHART_WIDTH
  );
  const numericHeight = toNumericDimension(
    typeof height === "number" ? height : containerSize.height,
    DEFAULT_CHART_HEIGHT
  );
  const layoutWidth = typeof width === "string" ? width : numericWidth;
  const layoutHeight = numericHeight;

  const hasData = data && data.datasets && data.datasets.length > 0 && 
    data.datasets.some(dataset => dataset.data && dataset.data.length > 0);

  const dataLength = data.labels?.length || 0;
  const needsHorizontalScroll = dataLength > 10 && (type === "bar" || type === "line");
  const scrollableWidth = needsHorizontalScroll
    ? Math.max(numericWidth, dataLength * 80)
    : layoutWidth;

  const processedData = {
    ...data,
    datasets: data.datasets?.map((dataset, index) => {
      // Apply colors if not already set
      if (!dataset.backgroundColor) {
        if (dataset.label?.includes("All Provider")) {
          dataset.backgroundColor = colors.allProvider;
        } else if (dataset.label?.includes("Ajay Huang")) {
          dataset.backgroundColor = colors.ajayHuang;
        } else if (dataset.label?.includes("Query Agreed")) {
          dataset.backgroundColor = colors.queryAgreed;
        } else if (dataset.label?.includes("Query Disagreed")) {
          dataset.backgroundColor = colors.queryDisagreed;
        }
      }

      // For line charts, also set borderColor and pointBackgroundColor
      if (type === "line") {
        if (!dataset.borderColor) {
          dataset.borderColor = dataset.backgroundColor;
        }
        if (!dataset.pointBackgroundColor) {
          dataset.pointBackgroundColor = dataset.backgroundColor;
        }
        if (dataset.pointBorderColor) {
          dataset.pointBorderColor = dataset.backgroundColor;
        }
        // Set line styling
        dataset.borderWidth = dataset.borderWidth || 3;
        dataset.pointRadius = dataset.pointRadius || 5;
        dataset.tension = dataset.tension || 0.4; // Smooth curves
        dataset.pointRadius = 5; // Instead of 5
dataset.pointBorderWidth = 2; 
      }

      return dataset;
    }) || [],
  };

  // Calculate legend height for pie charts
  const calculateLegendHeight = () => {
    if (type !== "pie" || !data.labels) return 0;

    const itemsPerRow = Math.max(
      1,
      Math.floor(numericWidth / 120)
    );
    const rows = Math.ceil(data.labels.length / itemsPerRow);
    return Math.max(rows * 30, 40);
  };

  // Calculate responsive pie chart size
  const getPieChartSize = () => {
    if (type !== "pie") return { width: "100%", height: "100%" };

    const legendHeight = calculateLegendHeight();
    const availableHeight = numericHeight - legendHeight - 40;
    const availableWidth = numericWidth - 40;

    const size = Math.min(availableHeight, availableWidth, 350);
    const finalSize = Math.max(size, 200);

    return {
      width: finalSize,
      height: finalSize,
    };
  };

 // Replace your existing useEffect with this fixed version
useEffect(() => {
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    .chartjs-legend-item span {
      border-radius: 8px !important;
    }
    .chart-scroll-container {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: thin;
      scrollbar-color: #e9ecef #f8f9fa;
    }
    .chart-scroll-container::-webkit-scrollbar {
      height: 8px;
    }
    .chart-scroll-container::-webkit-scrollbar-track {
      background: #f8f9fa;
      border-radius: 4px;
    }
    .chart-scroll-container::-webkit-scrollbar-thumb {
      background: #e9ecef;
      border-radius: 4px;
    }
    .chart-scroll-container::-webkit-scrollbar-thumb:hover {
      background: #dee2e6;
    }
    
    /* FIXED PIE LEGEND STYLES */
    .pie-legend-container {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 12px 20px;
      min-height: 40px;
      overflow-y: auto;
      max-height: 120px;
      align-content: flex-start;
      width: 100%;
    }
    
    .pie-legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      flex-shrink: 0;
      justify-content: flex-start;
      /* FIXED: Remove fixed width, use flexible sizing */
      flex: 0 0 auto;
      min-width: 120px;
      max-width: calc(50% - 10px);
    }
    
    /* SPECIAL HANDLING FOR 3 ITEMS */
    .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) {
      justify-content: space-around;
    }
    
    .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) .pie-legend-item {
      min-width: 100px;
      max-width: calc(33.333% - 10px);
      flex: 1 1 auto;
    }
    
    /* FOR 1 ITEM */
    .pie-legend-container:has(.pie-legend-item:only-child) .pie-legend-item {
      min-width: auto;
      max-width: none;
      flex: 0 0 auto;
    }
    
    /* FOR 2 ITEMS */
    .pie-legend-container:has(.pie-legend-item:nth-child(2):not(.pie-legend-item:nth-child(3))) .pie-legend-item {
      min-width: 140px;
      max-width: calc(50% - 10px);
      flex: 1 1 auto;
    }
    
    /* FOR 4+ ITEMS */
    .pie-legend-container:has(.pie-legend-item:nth-child(4)) .pie-legend-item {
      min-width: 140px;
      max-width: calc(50% - 10px);
      flex: 0 0 auto;
    }
    
    .pie-legend-color {
      width: 13px;
      height: 13px;
      border-radius: 5px;
      flex-shrink: 0;
    }
    
    .pie-legend-text {
      font-size: 13px;
      font-weight: 600;
      color: #495057;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    
    .pie-chart-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    
    .pie-chart-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
      min-height: 0;
    }
    
    .custom-legend-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 5px 20px 5px 0;
      min-height: 35px;
      gap: 20px;
    }
    
    .custom-legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      color: #495057;
    }
    
    .custom-legend-color {
      width: 16px;
      height: 16px;
      border-radius: 6px;
      flex-shrink: 0;
    }
    
    .chart-content-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }
    
    .chart-wrapper {
      flex: 1;
      position: relative;
      min-height: 0;
      padding: 10px;
    }
    
    @media (max-width: 768px) {
      .pie-legend-container {
        gap: 10px 15px;
        padding: 6px 8px;
      }
      
      .pie-legend-item {
        font-size: 11px;
        padding: 3px 6px;
        gap: 5px;
        min-width: 100px;
        max-width: calc(50% - 7px);
      }
      
      /* FOR 3 ITEMS ON MOBILE */
      .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) .pie-legend-item {
        min-width: 90px;
        max-width: calc(33.333% - 8px);
      }
      
      .pie-legend-color {
        width: 8px;
        height: 8px;
        border-radius: 2px;
      }
      
      .pie-legend-text {
        font-size: 11px;
      }
      
      .custom-legend-container {
        padding: 5px 15px 5px 0;
        gap: 15px;
      }
      
      .custom-legend-item {
        font-size: 13px;
        gap: 6px;
      }
      
      .custom-legend-color {
        width: 14px;
        height: 14px;
      }
    }
    
    @media (max-width: 793px) {
      .pie-legend-container {
        gap: 8px 10px;
        padding: 6px 4px;
      }
      
      .pie-legend-item {
        font-size: 10px;
        padding: 3px 4px;
        gap: 4px;
        min-width: 80px;
        max-width: calc(50% - 5px);
      }
      
      /* FOR 3 ITEMS - better distribution */
      .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) {
        justify-content: space-between;
      }
      
      .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) .pie-legend-item {
        min-width: 70px;
        max-width: calc(30% - 3px);
        flex: 1 1 auto;
      }
      
      .pie-legend-color {
        width: 8px;
        height: 8px;
        border-radius: 2px;
      }
      
      .pie-legend-text {
        font-size: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    
    @media (max-width: 480px) {
      .pie-legend-container {
        gap: 6px 8px;
        padding: 6px 4px;
        /* For small screens: keep horizontal layout for 3 items */
        flex-direction: row;
        justify-content: space-between;
      }
      
      .pie-legend-item {
        font-size: 9px;
        padding: 2px 3px;
        gap: 3px;
        min-width: 60px;
        max-width: calc(32% - 4px);
      }
      
      /* FOR 3 ITEMS ON SMALL SCREENS */
      .pie-legend-container:has(.pie-legend-item:nth-child(3):not(.pie-legend-item:nth-child(4))) .pie-legend-item {
        flex: 1 1 auto;
        min-width: 50px;
        max-width: calc(33.333% - 4px);
        text-align: center;
      }
      
      .pie-legend-color {
        width: 7px;
        height: 7px;
        border-radius: 2px;
        flex-shrink: 0;
      }
      
      .pie-legend-text {
        font-size: 9px;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.2;
      }
      
      .custom-legend-container {
        padding: 5px 12px 5px 0;
        gap: 12px;
        flex-wrap: wrap;
        justify-content: center;
      }
      
      .custom-legend-item {
        font-size: 12px;
        gap: 5px;
        min-width: calc(50% - 6px);
      }
      
      .custom-legend-color {
        width: 12px;
        height: 12px;
      }
    }
    
    @media (max-width: 380px) {
      .pie-legend-container {
        gap: 4px 6px;
        padding: 4px 2px;
        /* Very small screens: vertical layout might be better */
        flex-direction: column;
        align-items: center;
      }
      
      .pie-legend-item {
        font-size: 8px;
        padding: 2px;
        gap: 2px;
        min-width: 60px;
        max-width: 100%;
        justify-content: center;
        width: auto;
      }
      
      .pie-legend-color {
        width: 6px;
        height: 6px;
        border-radius: 1px;
        flex-shrink: 0;
      }
      
      .pie-legend-text {
        font-size: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.1;
      }
    }
  `;
  document.head.appendChild(styleElement);

  return () => {
    if (document.head.contains(styleElement)) {
      document.head.removeChild(styleElement);
    }
  };
}, []);

  const defaultOptions: ChartOptions<any> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // DISABLED - using custom legend instead
      },
      title: {
        display: false,
        text: title,
        font: {
          size: 16,
          weight: "bold",
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: '600',
          family: 'Roboto',
        },
        bodyFont: {
          size: 13,
          family: 'Roboto',
        },
        padding: {
          top: 12,
          bottom: 12,
          left: 16,
          right: 16,
        },
        callbacks: {
          title: function (context: any) {
            if (type === "pie") {
              return context[0].label || '';
            }
            return context[0].label || '';
          },
          label: function (context: any) {
            const label ='Value'
            const value = context.raw as number;
            
            if (type === "pie") {
              const label = context.dataset.label || '';

              const dataset = context.dataset;
              const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${formatNumber(value)} (${percentage}%)`;
            } else {
              return `${label}: ${formatNumber(value)}`;
            }
          },
          labelColor: function (context: any) {
            const dataset = context.dataset;
            let backgroundColor = dataset.backgroundColor;
            
            // Handle array of colors (for pie charts)
            if (Array.isArray(backgroundColor)) {
              backgroundColor = backgroundColor[context.dataIndex];
            }
            
            return {
              borderColor: backgroundColor,
              backgroundColor: backgroundColor,
              borderWidth: 2,
              borderRadius:  4, // Circular for line charts
            };
          },
          beforeLabel: function (context: any) {
            // Add category info for better context
            if (type === "bar" || type === "line") {
              return ``;
            }
            return '';
          },
          afterLabel: function () {
            return '';
          },
        },
        animation: {
          duration: 200,
        },
      },
      legendSquarePlugin: {},
    },
    layout: {
      padding: {
        top: 12,
        left: 8,
        right: 8,
        bottom: 8,
      },
    },
  };

  if (type !== "pie") {
    defaultOptions.scales = {
      x: {
        grid: {
          display: false,
        },
        title: buildAxisTitle(xAxisTitle),
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        title: buildAxisTitle(yAxisTitle),
      },
    };

    if (type === "bar" || type === "horizontalBar") {
      defaultOptions.barPercentage = 0.8;
      defaultOptions.categoryPercentage = 0.8;
      defaultOptions.elements = {
        bar: {
          borderRadius: {
            topLeft: 4,
            topRight: 4,
            bottomLeft: 0,
            bottomRight: 0,
          },
          borderSkipped: false,
        },
      };
    }
  }

  if (type === "pie") {
    defaultOptions.plugins = {
      ...defaultOptions.plugins,
      legend: {
        display: false, // We'll create custom legend
        onClick: () => {
          return false;
        },
      },
      tooltip: {
        ...defaultOptions.plugins.tooltip,
        callbacks: {
          ...defaultOptions.plugins.tooltip.callbacks,
          title: function (context: any) {
            return context[0].label || '';
          },
          label: function (context: any) {
            const value = context.raw as number;
            const dataset = context.dataset;
            const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `value ${percentage}%`;
          },
        
        },
      },
    };

    defaultOptions.radius = "90%";
    defaultOptions.layout = {
      padding: {
        top: 10,
        left: 10,
        right: 10,
        bottom: 10,
      },
    };
  }

  const chartOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options?.plugins,
    },
  };

  if (type !== "pie" && defaultOptions.scales) {
    chartOptions.scales = {
      ...(defaultOptions.scales || {}),
      ...(options?.scales || {}),
    };
  }

  if (type === "horizontalBar") {
    chartOptions.indexAxis = "y";
    chartOptions.barPercentage = 0.7;
    chartOptions.categoryPercentage = 0.8;

    chartOptions.elements = {
      ...chartOptions.elements,
      bar: {
        borderRadius: {
          topLeft: 0,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 4,
        },
        borderSkipped: false,
      },
    };
  }

  const containerHeight = layoutHeight;
  const containerWidth = layoutWidth;

  // Custom Legend Component for non-pie charts
  const CustomLegend = () => {
    if (type === "pie" || !data.datasets) return null;

    return (
      <div className="custom-legend-container">
        {data.datasets.map((dataset, index) => {
          // Get the appropriate color for the legend
          let legendColor = dataset.backgroundColor;
          
          // For line charts, prefer borderColor if available
          if (type === "line" && dataset.borderColor) {
            legendColor = dataset.borderColor;
          }
          
          // Handle array colors (take first color)
          if (Array.isArray(legendColor)) {
            legendColor = legendColor[0];
          }

          return (
            <div key={index} className="custom-legend-item">
              <div
                className="custom-legend-color"
                style={{
                  backgroundColor: legendColor as string
                }}
              />
              <span>{dataset.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom Pie Legend Component (non-clickable)
  const PieLegend = () => {
    if (type !== "pie" || !data.labels || !data.datasets[0]) return null;

    const labels = data.labels as string[];
    const colors = data.datasets[0].backgroundColor as string[];
    const values = data.datasets[0].data as number[];
    const total = values.reduce((sum, val) => sum + val, 0);

    return (
      <div className="pie-legend-container">
        {labels.map((label, index) => {
          const percentage = ((values[index] / total) * 100).toFixed(1);
          
          return (
            <div
              key={index}
              className="pie-legend-item"
              style={{ color: colors[index] }}
            >
              <div
                className="pie-legend-color"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="pie-legend-text">
                {label} ({percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const NoDataComponent = () => (
    <Center style={{ height: "100%", width: "100%" }}>
      <Stack align="center" gap="md">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke={themeColors.gray[4]}
          strokeWidth="1.5"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <path d="M9 9l6 6"/>
          <path d="M15 9l-6 6"/>
        </svg>
        <Text 
          size="lg" 
          c={themeColors.gray[6]} 
          fw={500}
          ta="center"
        >
          {noDataMessage}
        </Text>
        <Text 
          size="sm" 
          c={themeColors.gray[5]} 
          ta="center"
        >
          There's no data to display for this chart
        </Text>
      </Stack>
    </Center>
  );

  const renderChartSkeleton = () => {
    if (type === "pie") {
      return (
        <div
          style={{
            width: Math.min(350, numericWidth),
            height: Math.min(350, numericHeight),
            margin: "0 auto",
            position: "relative",
          }}
        >
          <Skeleton
            animate
            mt={"40px"}
            circle
            height={Math.min(250, numericHeight)}
          />
        </div>
      );
    } else if (type === "line") {
      return (
        <Box style={{ width: "100%", height: "100%", padding: "24px 12px" }}>
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <Skeleton animate height={16} width={80} radius='md' />
            <Skeleton animate height={16} width={80} radius='md' />
          </Box>

          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 40px)",
            }}
          >
            <Box
              style={{
                position: "relative",
                width: "100%",
                height: "calc(100% - 20px)",
                padding: "0 10px",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <Skeleton animate key={i} height={8} width={20} radius='md' />
                ))}
              </Box>

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  paddingLeft: "30px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <svg
                  width='100%'
                  height='100%'
                  viewBox='0 0 100 100'
                  preserveAspectRatio='none'
                >
                  <path
                    d='M0,50 L10,30 L20,70 L30,20 L40,60 L50,40 L60,80 L70,30 L80,50 L90,10 L100,40'
                    fill='none'
                    stroke='#f1f3f5'
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeDasharray='1, 5'
                  />
                </svg>

                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    paddingLeft: "30px",
                  }}
                >
                  <svg
                    width='100%'
                    height='100%'
                    viewBox='0 0 100 100'
                    preserveAspectRatio='none'
                  >
                    {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(
                      (x, i) => {
                        const yValues = [
                          50, 30, 70, 20, 60, 40, 80, 30, 50, 10, 40,
                        ];
                        return (
                          <circle
                            key={i}
                            cx={x}
                            cy={yValues[i]}
                            r='2'
                            fill='#e9ecef'
                          />
                        );
                      }
                    )}
                  </svg>
                </div>
              </div>

              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  paddingLeft: "30px",
                }}
              >
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    animate
                    key={i}
                    height={10}
                    width={30}
                    radius='md'
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } else {
      const isHorizontal = type === "horizontalBar";

      return (
        <Box style={{ width: "100%", height: "100%", padding: "24px 12px" }}>
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <Skeleton animate height={16} width={80} radius='lg' />
            <Skeleton animate height={16} width={80} radius='lg' />
          </Box>

          <Box
            style={{
              display: "flex",
              height: "calc(100% - 40px)",
              flexDirection: isHorizontal ? "row" : "row",
            }}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: isHorizontal ? "row" : "row",
                justifyContent: "space-around",
                alignItems: "flex-end",
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              {[...Array(6)].map((_, i) => {
                const randomHeight = 30 + Math.random() * 70;
                return isHorizontal ? (
                  <Skeleton
                    key={i}
                    height={`${randomHeight}%`}
                    width={40}
                    style={{ marginRight: "24px" }}
                    animate
                  />
                ) : (
                  <Skeleton
                    key={i}
                    height={`${randomHeight}%`}
                    width={40}
                    style={{ marginRight: "24px" }}
                    animate
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Paper radius='md' style={{ boxShadow: `6px 6px 6px ${gray[2]}` }}>
      <Box
        pl={10}
        p={12}
        style={{
          display: "flex",
          justifyContent: "flex-start",
          borderRadius: "8px 8px 0px 0px",
          backgroundColor: gray[2],
        }}
      >
        <Text c={gray[8]} size='md' fw={500}>
          {title}
        </Text>
      </Box>
      <Box
        ref={containerRef}
        h={containerHeight}
        w={containerWidth}
        style={{
          backgroundColor: "white",
          borderRadius: "0 0 8px 8px",
        }}
      >
        {loading ? (
          renderChartSkeleton()
        ) : !hasData || noData ? (
          <NoDataComponent />
        ) : (
          <div className="chart-content-wrapper">
            {/* Custom Legend for non-pie charts */}
            <CustomLegend />
            
            {/* Chart Container */}
            <div className="chart-wrapper">
              {type === "pie" ? (
                <div className="pie-chart-container">
                  <PieLegend />
                  <div className="pie-chart-wrapper">
                    <div
                      style={{
                        width: getPieChartSize().width,
                        height: getPieChartSize().height,
                      }}
                    >
                      <Pie
                        ref={chartRef}
                        data={processedData as ChartData<"pie">}
                        options={chartOptions as ChartOptions<"pie">}
                        plugins={[legendSquarePlugin, piePercentagePlugin]}
                      />
                    </div>
                  </div>
                </div>
              ) : needsHorizontalScroll ? (
                <div 
                  className="chart-scroll-container"
                  style={{
                    width: "100%",
                    height: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "10px 0",
                  }}
                >
                  <div style={{ width: scrollableWidth, height: "100%" }}>
                    {type === "bar" && (
                      <Bar
                        ref={chartRef}
                        data={processedData as ChartData<"bar">}
                        options={chartOptions as ChartOptions<"bar">}
                        plugins={[legendSquarePlugin]}
                      />
                    )}
                    {type === "line" && (
                      <Line
                        ref={chartRef}
                        data={processedData as ChartData<"line">}
                        options={chartOptions as ChartOptions<"line">}
                        plugins={[legendSquarePlugin]}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div 
                  style={{ 
                    width: "100%", 
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  {(type === "bar" || type === "horizontalBar") && (
                    <Bar
                      ref={chartRef}
                      data={processedData as ChartData<"bar">}
                      options={chartOptions as ChartOptions<"bar">}
                      plugins={[legendSquarePlugin]}
                    />
                  )}
                  {type === "line" && (
                    <Line
                      ref={chartRef}
                      data={processedData as ChartData<"line">}
                      options={chartOptions as ChartOptions<"line">}
                      plugins={[legendSquarePlugin]}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Box>
    </Paper>
  );
};

export default DataChart;